import { apiClient } from '@/api/client';
import type { PaginatedResponse } from '@/lib/types/api.types';

/** One live offer as a member sees it (Backend `PerkCardDto`, FR-30). Whitelisted: no internal fields. */
export interface PerkCard {
  id: string;
  title: string;
  subtitle: string | null;
  description: string;
  /** Presigned GET URL, or null. */
  coverPhotoUrl: string | null;
  expiresAt: string | null;
  /** Where Claim Offer opens (http/https), or null (FR-114). */
  claimLinkUrl: string | null;
  /** Opens the member's mail app, or null. With neither, no claim action is shown. */
  claimEmail: string | null;
  partner: { entityId: string; businessName: string; logoUrl: string | null };
}

/** One directory card (Backend `DirectoryPartnerCardDto`). Carries no contact action (FR-109). */
export interface DirectoryPartnerCard {
  entityId: string;
  businessName: string;
  tagline: string | null;
  logoUrl: string | null;
  /** The detail-page cover (FR-116). */
  coverPhotoUrl: string | null;
  /** Optional directory-card background; null falls back to the detail cover, then the brand gradient (FR-116). */
  listingCoverUrl: string | null;
  /** First assigned category by name, or null. */
  category: string | null;
  isFeatured: boolean;
  hasOffer: boolean;
}

/** The single contact method a partner chose (FR-108/109); the unused one is never sent. */
export type PartnerContact =
  | { type: 'email'; address: string }
  | { type: 'scheduling'; url: string };

export interface DirectoryPartnerDetail extends DirectoryPartnerCard {
  description: string;
  websiteUrl: string;
  linkedinUrl: string | null;
  promoVideoUrl: string | null;
  categories: string[];
  services: string[];
  contact: PartnerContact;
  offers: PerkCard[];
  /**
   * Resources this partner provided (FR-294, MYN-189), newest first and
   * capped by the backend. Only what the library itself shows. Empty means
   * the section is not rendered.
   */
  providedResources: PartnerProvidedResource[];
}

/** One provided resource as the partner profile lists it (FR-294). */
export interface PartnerProvidedResource {
  id: string;
  title: string;
  resourceType: string;
  /** The topic folder it stays filed under - never recategorised by partner (FR-279). */
  folderName: string | null;
}

export interface DirectoryQuery {
  page: number;
  pageSize: number;
  search?: string;
  categoryId?: string;
}

export async function fetchDirectory(
  query: DirectoryQuery,
): Promise<PaginatedResponse<DirectoryPartnerCard>> {
  const { data } = await apiClient.get<PaginatedResponse<DirectoryPartnerCard>>('/trusted-partners', {
    params: query,
  });
  return data;
}

export async function fetchPartnerDetail(entityId: string): Promise<DirectoryPartnerDetail> {
  const { data } = await apiClient.get<DirectoryPartnerDetail>(`/trusted-partners/${entityId}`);
  return data;
}

export async function fetchLivePerks(): Promise<PerkCard[]> {
  const { data } = await apiClient.get<PerkCard[]>('/perks');
  return data;
}

/** Counts and logs one Claim Offer click (FR-30, FR-112). The Backend answers 409 when the offer is no longer live, and 204 (counting nothing) for the partner's own members. */
export async function redeemPerk(offerId: string): Promise<void> {
  await apiClient.post(`/perks/${offerId}/redeem`);
}

/** Counts and logs one Contact Us click on an offer (FR-119). Same 409 / 204 answers as `redeemPerk`. */
export async function contactPerk(offerId: string): Promise<void> {
  await apiClient.post(`/perks/${offerId}/contact`);
}

/** Counts and logs one Schedule click (FR-110, FR-118). */
export async function recordSchedulingClick(entityId: string): Promise<void> {
  await apiClient.post(`/trusted-partners/${entityId}/scheduling-click`);
}

/** Counts and logs one email-contact click on a partner profile (FR-118). */
export async function recordEmailClick(entityId: string): Promise<void> {
  await apiClient.post(`/trusted-partners/${entityId}/email-click`);
}
